"use client";

import { useEffect } from "react";

import { armChunkRecoveryConfirmation, requestChunkReload } from "@/lib/chunk-recovery";

/**
 * Deployment-skew resilience — belt-and-suspenders to the R2 chunk retention.
 *
 * The edge worker already serves a prior deploy's `/_next/static` chunks from
 * R2 on an Assets 404 (180-day retention), so the common skew case is fixed
 * server-side and Googlebot never sees a broken render. This handles the rare
 * residual cases retention can't: a tab open PAST the 180-day window, or any
 * retention gap. When a chunk genuinely can't load, React/Next throw a
 * ChunkLoadError (or a <script>/<link> resource error); a single hard reload
 * pulls the current deploy's HTML + chunks.
 *
 * Loop-guarded: we reload at most once per cooldown window, so a genuinely
 * broken new deploy degrades to "show the error" instead of spinning forever.
 * Scoped to /_next/static so third-party (analytics/pixel) load failures never
 * trigger a reload. Client-only behavior — a no-op for crawlers (which, thanks
 * to retention, don't hit a chunk 404 in the first place).
 */

function looksLikeChunkError(message: string): boolean {
  return (
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /(error|failed to fetch) .*dynamically imported module/i.test(message)
  );
}

function staticAssetPath(target: EventTarget | null): string | undefined {
  const el = target as (HTMLScriptElement & HTMLLinkElement) | null;
  if (!el || (el.tagName !== "SCRIPT" && el.tagName !== "LINK")) return undefined;
  const url = el.src || el.href || "";
  if (!url.includes("/_next/static/")) return undefined;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }
}

export default function ChunkErrorReloader() {
  useEffect(() => {
    const cancelConfirmation = armChunkRecoveryConfirmation();
    function onError(event: ErrorEvent) {
      const assetPath = staticAssetPath(event.target);
      const runtimeChunkError = looksLikeChunkError(event.message || String(event.error ?? ""));
      if (!assetPath && !runtimeChunkError) return;
      if (
        requestChunkReload({
          trigger: assetPath ? "resource" : "runtime",
          assetPath,
        })
      )
        window.location.reload();
    }
    function onRejection(event: PromiseRejectionEvent) {
      const reason = event.reason as { name?: string; message?: string } | undefined;
      const message =
        reason?.name === "ChunkLoadError"
          ? "ChunkLoadError"
          : String(reason?.message ?? reason ?? "");
      if (looksLikeChunkError(message)) {
        if (requestChunkReload({ trigger: "rejection" })) window.location.reload();
      }
    }
    // Capture phase: resource-load errors (script/link onerror) do not bubble,
    // so a non-capturing window listener would miss them.
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      cancelConfirmation();
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
